import { HStack, Link as ChakraLink, Text as ChakraText, Tooltip } from '@chakra-ui/react';
import { Link } from 'react-router-dom';

import useSandboxTheme from '../useSandboxTheme';

interface ICharacterCounterProps {
  currentChars: number;
  maxChars: number;
  isFree: boolean;
}

/**
 * Character counter for code editor footer.
 * Only shows when approaching limit (70%+).
 * Subtle gray styling with tooltip explanation.
 */
const CharacterCounter = ({ currentChars, maxChars, isFree }: ICharacterCounterProps) => {
  const { colors } = useSandboxTheme();
  const percentage = (currentChars / maxChars) * 100;

  // Only show when at 70% or above
  if (percentage < 70) {
    return null;
  }

  // Color coding: yellow 70-90%, red > 90%
  let color = colors.textMuted;
  let tooltipText = `You're using ${Math.round(percentage)}% of your character limit for this tab.`;

  if (percentage >= 90) {
    color = 'red.400';
    tooltipText = `Warning: You're at ${Math.round(percentage)}% of your character limit. Code beyond the limit won't be saved.`;
  } else if (percentage >= 70) {
    color = 'yellow.500';
    tooltipText = `You're approaching your character limit (${Math.round(percentage)}%).`;
  }

  return (
    <Tooltip
      label={tooltipText}
      placement="top"
      hasArrow
      bg={colors.popoverBg}
      color={colors.textPrimary}
      fontSize="xs"
      px={3}
      py={2}
      borderWidth="1px"
      borderColor={colors.popoverBorder}
    >
      <HStack spacing={2} cursor="default">
        <ChakraText fontSize="xs" color={color} fontFamily="mono">
          {currentChars.toLocaleString()} / {maxChars.toLocaleString()}
        </ChakraText>
        {isFree && percentage >= 80 && (
          <ChakraLink
            as={Link}
            to="/pricing"
            fontSize="xs"
            color="purple.300"
            _hover={{ color: 'purple.200', textDecoration: 'underline' }}
          >
            Upgrade
          </ChakraLink>
        )}
      </HStack>
    </Tooltip>
  );
};

export default CharacterCounter;
